import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { ArrowLeft, CheckCircle, XCircle, MinusCircle, ChevronDown, ChevronUp } from 'lucide-react';
import 'katex/dist/katex.min.css';
import katex from 'katex';

const renderFormula = (formula) => {
  if (!formula) return '';
  try {
    return katex.renderToString(formula, { throwOnError: false, displayMode: true });
  } catch {
    return formula;
  }
};

const SUBJECTS = ['Mathematics', 'Physics', 'Chemistry'];

export default function ReviewPage() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [subjectFilter, setSubjectFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    fetchReview();
  }, [testId]);

  const fetchReview = async () => {
    try {
      const { data: res } = await api.get(`/results/review/${testId}`);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load review');
      navigate(`/student/result/${testId}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center"><div className="loader"></div></div>
  );
  if (!data) return null;

  const { result, questions } = data;

  const filtered = questions.filter(q => {
    if (subjectFilter !== 'all' && q.subject !== subjectFilter) return false;
    if (filter === 'correct' && !q.isCorrect) return false;
    if (filter === 'incorrect' && (q.isCorrect || q.selectedAnswer === null)) return false;
    if (filter === 'unattempted' && q.selectedAnswer !== null) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4">
      <div className="max-w-3xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(`/student/result/${testId}`)} className="text-gray-500 hover:text-gray-800">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Answer Review</h1>
            <p className="text-gray-500 text-sm">{result?.test?.title}</p>
          </div>
        </div>

        {/* Score Summary */}
        <div className="card flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle size={16} /> <span className="font-bold">{result.correctAnswers}</span> Correct
          </div>
          <div className="flex items-center gap-2 text-red-500">
            <XCircle size={16} /> <span className="font-bold">{result.incorrectAnswers}</span> Incorrect
          </div>
          <div className="flex items-center gap-2 text-gray-400">
            <MinusCircle size={16} /> <span className="font-bold">{result.unattempted}</span> Unattempted
          </div>
          <div className="ml-auto font-bold text-primary-700">Score: {result.totalScore}/{result.maxScore}</div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'all', label: 'All' },
            { id: 'correct', label: '✅ Correct' },
            { id: 'incorrect', label: '❌ Incorrect' },
            { id: 'unattempted', label: '○ Unattempted' }
          ].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filter === f.id ? 'bg-primary-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              {f.label}
            </button>
          ))}
          <select className="input-field w-auto text-sm py-1.5" value={subjectFilter} onChange={e => setSubjectFilter(e.target.value)}>
            <option value="all">All Subjects</option>
            {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <p className="text-sm text-gray-500">{filtered.length} questions shown</p>

        {/* Questions */}
        <div className="space-y-3">
          {filtered.map((q, i) => {
            const isExpanded = expanded === q._id;
            const statusIcon = q.selectedAnswer === null
              ? <MinusCircle className="text-gray-400" size={18} />
              : q.isCorrect
              ? <CheckCircle className="text-green-500" size={18} />
              : <XCircle className="text-red-500" size={18} />;

            const borderColor = q.selectedAnswer === null
              ? 'border-gray-200'
              : q.isCorrect ? 'border-green-200' : 'border-red-200';

            const bgColor = q.selectedAnswer === null
              ? 'bg-white'
              : q.isCorrect ? 'bg-green-50/50' : 'bg-red-50/50';

            return (
              <div key={q._id} className={`border ${borderColor} ${bgColor} rounded-xl overflow-hidden`}>
                {/* Question header */}
                <button className="w-full p-4 flex items-start gap-3 text-left"
                  onClick={() => setExpanded(isExpanded ? null : q._id)}>
                  {statusIcon}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded font-medium">Q.{q.questionNumber}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        q.subject === 'Mathematics' ? 'bg-blue-100 text-blue-700' :
                        q.subject === 'Physics' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                      }`}>{q.subject}</span>
                      <span className={`ml-auto text-xs font-bold ${q.marks > 0 ? 'text-green-600' : q.marks < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                        {q.marks > 0 ? '+' : ''}{q.marks}
                      </span>
                    </div>
                    <p className="text-gray-800 text-sm line-clamp-2">{q.questionText}</p>
                  </div>
                  {isExpanded ? <ChevronUp size={16} className="text-gray-400 flex-shrink-0" /> : <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />}
                </button>

                {/* Expanded content */}
                {isExpanded && (
                  <div className="border-t border-gray-100 p-4 space-y-4">
                    {/* Formula */}
                    {q.formula && (
                      <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg overflow-x-auto">
                        <div dangerouslySetInnerHTML={{ __html: renderFormula(q.formula) }} />
                      </div>
                    )}

                    {/* Options */}
                    <div className="space-y-2">
                      {['A', 'B', 'C', 'D'].map(opt => {
                        const isCorrectOpt = opt === q.correctAnswer;
                        const isSelectedOpt = opt === q.selectedAnswer;
                        let optClass = 'border-gray-200 bg-gray-50 text-gray-700';
                        if (isCorrectOpt) optClass = 'border-green-400 bg-green-50 text-green-800';
                        else if (isSelectedOpt && !isCorrectOpt) optClass = 'border-red-400 bg-red-50 text-red-800';

                        return (
                          <div key={opt} className={`flex items-center gap-3 px-3 py-2 rounded-lg border-2 ${optClass}`}>
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              isCorrectOpt ? 'bg-green-500 text-white' :
                              isSelectedOpt ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-600'
                            }`}>{opt}</span>
                            <span className="flex-1 text-sm">{q.options?.[opt]}</span>
                            <div className="flex gap-1">
                              {isCorrectOpt && <span className="text-xs text-green-600 font-medium">✓ Correct</span>}
                              {isSelectedOpt && !isCorrectOpt && <span className="text-xs text-red-600 font-medium">✗ Your answer</span>}
                              {isSelectedOpt && isCorrectOpt && <span className="text-xs text-green-600 font-medium">✓ Your answer</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Status */}
                    {q.selectedAnswer === null && (
                      <div className="text-sm text-gray-500 bg-gray-100 rounded-lg p-3">
                        <strong>Not attempted.</strong> Correct answer: <strong className="text-green-700">{q.correctAnswer}. {q.options?.[q.correctAnswer]}</strong>
                      </div>
                    )}

                    {/* Explanation */}
                    {q.explanation && (
                      <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                        <p className="text-xs font-semibold text-blue-700 mb-1">💡 Explanation</p>
                        <p className="text-sm text-blue-800">{q.explanation}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-10 text-gray-400">
            <p>No questions match the selected filter</p>
          </div>
        )}
      </div>
    </div>
  );
}
